import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, useLocation, useNavigate } from 'react-router';
import { useGetAliveQuery } from '../alive/aliveEndpoints';
import { useAuthServices } from '../auth/authServices';
import { useGetMeQuery } from '../auth/authEndpoints';
import { Icon } from '../common/Icon';

/**
 * The signed-in admin's navbar (QZ-D18, QZ-D47, QZ-D58): the app name with its environment
 * badge, the page links, their email and sign-out. The environment comes from `/alive`, since
 * `config.json` carries none (QZ-D10); until it answers there is no badge.
 *
 * Below Bootstrap's `lg` the links and the user collapse behind a menu button and open as a
 * list under the bar, pushing the page down. The toggle is React state, not Bootstrap's collapse
 * plugin, whose JavaScript isn't loaded. The menu remembers the location it was opened at, so
 * following a link (any navigation) closes it without an effect.
 */
export const Navbar = () => {
  const { t } = useTranslation();
  const { session } = useAuthServices();
  const navigate = useNavigate();
  const location = useLocation();
  const { data } = useGetMeQuery();
  const { data: alive } = useGetAliveQuery();
  const [openAt, setOpenAt] = useState<string | null>(null);
  const menuOpen = openAt === location.key;

  const handleSignOut = async () => {
    await session.signOut();
    navigate('/signin', { replace: true });
  };

  return (
    <nav className="qz-navbar">
      <div className="qz-container qz-navbar-inner">
        <Link className="qz-brand" to="/">
          <span className="qz-brand-mark" aria-hidden="true">
            ?
          </span>
          {t('nav.appName')}
        </Link>
        {alive && <span className="qz-env">{alive.envType}</span>}
        <button
          type="button"
          className="btn btn-sm qz-btn-nav qz-nav-toggle"
          aria-label={t('nav.menu')}
          aria-expanded={menuOpen}
          aria-controls="qz-nav-menu"
          onClick={() => setOpenAt(menuOpen ? null : location.key)}
        >
          <Icon name={menuOpen ? 'xmark' : 'bars'} />
        </button>
        <div id="qz-nav-menu" className={`qz-nav-menu${menuOpen ? ' is-open' : ''}`}>
          <div className="qz-nav-links">
            <NavLink to="/tasks">{t('nav.tasks')}</NavLink>
            <NavLink to="/jobs">{t('nav.jobs')}</NavLink>
            <NavLink to="/user-jobs">{t('nav.userJobs')}</NavLink>
            <NavLink to="/tags">{t('nav.tags')}</NavLink>
            <NavLink to="/sources">{t('nav.sources')}</NavLink>
            <NavLink to="/job-configs">{t('nav.jobConfigs')}</NavLink>
          </div>
          <div className="qz-nav-user">
            {data?.email && (
              <span className="qz-nav-email" title={data.email}>
                {data.email}
              </span>
            )}
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary qz-btn-nav"
              onClick={handleSignOut}
            >
              {t('nav.signOut')}
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
